import { ModalManager } from '../modalManager/_modalManager';
import { v4 as uuidv4 } from 'uuid';

interface DebugOptions {
  enabled: boolean;
}

const DEBUG_DEFAULT: DebugOptions = {
  enabled: false, // デフォルトでは無効化
};

class DebugUtil {
  private enabled: boolean;
  private prefix: string;

  constructor(enabled: boolean, prefix: string = '') {
    this.enabled = enabled;
    this.prefix = prefix ? `[${prefix}] ` : '';
  }

  log(...args: any[]): void {
    if (this.enabled) {
      console.debug(this.prefix, ...args);
    }
  }
}

interface MapData {
  alt: string;
  shape: string;
  coords: string;
  href?: string;
  action: {
    'data-aria-label': string;
    content: string;
  };
}

interface ImageMapConfig {
  mapData: MapData[];
}

interface LinkPosition {
  left: number;
  top: number;
  width: number;
  height: number;
}

export class ImageMap {
  private container: Element;
  private data: ImageMapConfig | null = null;
  private actionContainer: HTMLDivElement | null = null;
  private boundHandleResize: () => void;
  private debug: DebugUtil;
  private modalManager: ModalManager;
  private isModalOpen: boolean = false;

  // パフォーマンス最適化のためのキャッシュ
  private linkPositionCache: LinkPosition[] = [];
  private currentHoverTimer: number | null = null;
  private lastImageSize: { width: number; height: number } | null = null;

  // イベント委譲用のハンドラー（コンストラクタで初期化）
  private boundHandleClick!: (e: Event) => void;
  private boundHandleKeydown!: (e: Event) => void;
  private boundHandleMouseenter!: (e: Event) => void;
  private boundHandleMouseleave!: (e: Event) => void;
  private boundHandleGlobalKeyDown!: (event: KeyboardEvent) => void;

  constructor(container: Element, debugOptions: DebugOptions = DEBUG_DEFAULT) {
    if (!container) {
      throw new Error('コンテナ要素が見つかりません');
    }

    if (container.querySelector('.ist-js__imageMapActionContents')) {
      throw new Error('このコンテナでImageMapは既に初期化されています。');
    }

    this.container = container;
    this.debug = new DebugUtil(debugOptions.enabled, 'ImageMap');
    this.modalManager = new ModalManager();

    // イベントハンドラーを初期化
    this.boundHandleClick = this.handleClick.bind(this);
    this.boundHandleKeydown = this.handleKeydown.bind(this);
    this.boundHandleMouseenter = this.handleMouseenter.bind(this);
    this.boundHandleMouseleave = this.handleMouseleave.bind(this);
    this.boundHandleGlobalKeyDown = this.handleGlobalKeyDown.bind(this);

    // デバウンス時間を増やしてパフォーマンス向上
    this.boundHandleResize = this.debounce(
      this.updatePositions.bind(this),
      500
    );

    this.init();
  }

  private init(): void {
    const jsonScript = this.container.querySelector('.ist-imageMap__data');
    if (!jsonScript) {
      this.debug.log('マップデータ要素が見つかりません');
      return;
    }

    try {
      this.data = JSON.parse(jsonScript.textContent || '');

      if (
        !this.data?.mapData ||
        !Array.isArray(this.data?.mapData) ||
        this.data?.mapData.length === 0
      ) {
        this.debug.log('mapDataが空または無効のため、初期化を中断します');
        return;
      }

      const img = this.container.querySelector('img');
      if (!img) {
        this.debug.log('コンテナ内に画像が見つかりません');
        return;
      }

      if (img.complete) {
        this.setupMap();
      } else {
        img.addEventListener('load', () => this.setupMap(), { once: true });
      }
    } catch (e) {
      console.error('マップデータの解析に失敗:', e);
    }
  }

  private setupMap(): void {
    this.createActionContainer();
    if (this.actionContainer) {
      this.setupEventListeners();
      this.updatePositions();
    }
  }

  private createActionContainer(): void {
    if (!this.data) return;

    this.actionContainer = document.createElement('div');
    this.actionContainer.classList.add('ist-js__imageMapActionContents');

    // DocumentFragmentを使用してDOM操作を最適化
    const fragment = document.createDocumentFragment();

    this.data.mapData.forEach((item, index) => {
      const link = document.createElement('a');
      link.setAttribute('href', item.href || '#');
      link.setAttribute(
        'aria-label',
        item.action['data-aria-label'] || item.alt
      );
      link.setAttribute('role', 'link');
      link.setAttribute('tabindex', '0');
      link.setAttribute('data-index', index.toString());
      link.classList.add('ist-imageMap__actionLink');

      fragment.appendChild(link);
    });

    this.actionContainer.appendChild(fragment);
    this.container.appendChild(this.actionContainer);
  }

  private setupEventListeners(): void {
    if (!this.actionContainer) return;

    // イベント委譲を使用してパフォーマンス向上
    this.actionContainer.addEventListener('click', this.boundHandleClick);
    this.actionContainer.addEventListener('keydown', this.boundHandleKeydown);
    this.actionContainer.addEventListener(
      'mouseenter',
      this.boundHandleMouseenter,
      true
    );
    this.actionContainer.addEventListener(
      'mouseleave',
      this.boundHandleMouseleave,
      true
    );

    window.addEventListener('resize', this.boundHandleResize, {
      passive: true,
    });
    document.addEventListener('keydown', this.boundHandleGlobalKeyDown);
  }

  private handleClick(e: Event): void {
    const link = this.getLinkFromEvent(e);
    if (!link) return;

    e.preventDefault();
    this.clearHoverTimer();
    this.openModalForLink(link);
  }

  private handleKeydown(e: Event): void {
    const keyboardEvent = e as KeyboardEvent;
    const link = this.getLinkFromEvent(e);
    if (!link) return;

    if (keyboardEvent.key === 'Enter' || keyboardEvent.key === ' ') {
      e.preventDefault();
      this.clearHoverTimer();
      this.openModalForLink(link);
    }
  }

  private handleMouseenter(e: Event): void {
    const link = this.getLinkFromEvent(e);
    if (!link || this.isModalOpen) return;

    this.clearHoverTimer();

    // タイマー処理を簡素化
    if (!this.currentHoverTimer) {
      this.currentHoverTimer = window.setTimeout(() => {
        if (!this.isModalOpen) {
          this.openModalForLink(link);
        }
        this.currentHoverTimer = null;
      }, 300);
    }
  }

  private handleMouseleave(e: Event): void {
    const link = this.getLinkFromEvent(e);
    if (!link) return;

    this.clearHoverTimer();
  }

  private getLinkFromEvent(e: Event): HTMLAnchorElement | null {
    const target = e.target as HTMLElement;
    if (target.classList.contains('ist-imageMap__actionLink')) {
      return target as HTMLAnchorElement;
    }
    return null;
  }

  private clearHoverTimer(): void {
    if (this.currentHoverTimer) {
      clearTimeout(this.currentHoverTimer);
      this.currentHoverTimer = null;
    }
  }

  private openModalForLink(link: HTMLAnchorElement): void {
    if (this.isModalOpen) return;

    const index = parseInt(link.getAttribute('data-index') || '0', 10);
    const mapData = this.data?.mapData[index];

    if (!mapData?.action.content) return;

    const uniqueModalId = `modal-${uuidv4()}`;

    this.modalManager.showModal({
      content: mapData.action.content,
      modalId: uniqueModalId,
      modalAriaLabel: mapData.action['data-aria-label'],
      triggerElement: link,
      onClose: () => {
        link.focus();
        this.isModalOpen = false;
      },
    });

    this.isModalOpen = true;
  }

  private handleGlobalKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      // ModalManagerが処理するため、特に処理不要
    }
  }

  private debounce(func: Function, wait: number): () => void {
    let timeout: ReturnType<typeof setTimeout>;
    return () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(), wait);
    };
  }

  private updatePositions(): void {
    const img = this.container.querySelector('img');
    if (!img || !this.data || !this.actionContainer) return;

    const currentWidth = img.offsetWidth;
    const currentHeight = img.offsetHeight;

    // サイズが変わっていない場合はスキップ
    if (
      this.lastImageSize &&
      this.lastImageSize.width === currentWidth &&
      this.lastImageSize.height === currentHeight
    ) {
      return;
    }

    const naturalWidth = img.naturalWidth;
    const scale = currentWidth / naturalWidth;
    const links = this.actionContainer.querySelectorAll('a');

    // バッチでDOM操作を実行
    requestAnimationFrame(() => {
      links.forEach((link, index) => {
        const mapData = this.data?.mapData[index];
        if (!mapData) return;

        // 位置計算をキャッシュから取得または計算
        let position = this.linkPositionCache[index];
        if (!position) {
          const coords = mapData.coords
            .split(',')
            .map((num) => parseInt(num, 10));
          const [x1, y1, x2, y2] = coords;
          position = {
            left: x1,
            top: y1,
            width: x2 - x1,
            height: y2 - y1,
          };
          this.linkPositionCache[index] = position;
        }

        const linkElement = link as HTMLElement;
        const scaledLeft = position.left * scale;
        const scaledTop = position.top * scale;
        const scaledWidth = position.width * scale;
        const scaledHeight = position.height * scale;

        // スタイルをまとめて設定
        linkElement.style.cssText = `
          position: absolute;
          left: ${scaledLeft}px;
          top: ${scaledTop}px;
          width: ${scaledWidth}px;
          height: ${scaledHeight}px;
        `;
      });
    });

    this.lastImageSize = { width: currentWidth, height: currentHeight };
  }

  public destroy(): void {
    this.clearHoverTimer();

    if (this.actionContainer) {
      this.actionContainer.removeEventListener('click', this.boundHandleClick);
      this.actionContainer.removeEventListener(
        'keydown',
        this.boundHandleKeydown
      );
      this.actionContainer.removeEventListener(
        'mouseenter',
        this.boundHandleMouseenter,
        true
      );
      this.actionContainer.removeEventListener(
        'mouseleave',
        this.boundHandleMouseleave,
        true
      );
    }

    window.removeEventListener('resize', this.boundHandleResize);
    document.removeEventListener('keydown', this.boundHandleGlobalKeyDown);

    this.modalManager.hideModal();

    if (this.actionContainer) {
      this.actionContainer.remove();
      this.actionContainer = null;
    }

    this.isModalOpen = false;
    this.linkPositionCache = [];
    this.lastImageSize = null;
  }
}
